import { useState, useEffect } from 'react'
import algoliasearch from 'algoliasearch/lite'
import {
  InstantSearch,
  SearchBox,
  Hits,
  Configure,
  useInstantSearch,
  useClearRefinements,
} from 'react-instantsearch'

import SearchResultsHit from './search-results-hit'
import SearchResultsStats from './search-results-stats'
import AlgoliaIndexPagination from '../../archives/algolia-index-pagination'

const getUrlParam = () => {
  const urlParams = new URLSearchParams(window.location.search)
  const queryParam = urlParams.get('s')

  if (!queryParam) return ''

  return queryParam
}

function AlgoliaNoResults(): React.ReactNode {
  const { refine: clearRefinement } = useClearRefinements({
    includedAttributes: ['query'],
  })

  return (
    <div className="text-center my-[10%]">
      <h3 className="mb-sm">No results found</h3>
      <p className="p--lg">
        Please try a different search term or browse our categories
      </p>

      <button
        onClick={() => {
          clearRefinement()
        }}
        className="btn btn--sm btn--solid py-sm px-lg w-auto whitespace-nowrap mt-lg">
        Clear and try again
      </button>
    </div>
  )
}

function NoResultsBoundary({ children, fallback }) {
  const { results } = useInstantSearch()

  // The `__isArtificial` flag makes sure not to display the No Results message
  // when no hits have been returned.
  if (!results.__isArtificial && results.nbHits === 0) {
    return <>{fallback}</>
  }

  return children
}

export default function AlgoliaSearchResults() {
  const [searchClient, setSearchClient] = useState(null)

  useEffect(() => {
    // Check if we're in the browser and API keys are available
    if (
      typeof window !== 'undefined' &&
      window.ALGOLIA_APPLICATION_ID &&
      window.ALGOLIA_SEARCH_API_KEY
    ) {
      const client = algoliasearch(
        window.ALGOLIA_APPLICATION_ID,
        window.ALGOLIA_SEARCH_API_KEY,
      )
      setSearchClient(client)
    }
  }, [])

  // Don't render InstantSearch until we have a valid search client
  if (!searchClient) {
    return null
  }

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName="wp_searchable_posts"
      future={{
        preserveSharedStateOnUnmount: true,
        persistHierarchicalRootCount: true,
      }}
      routing={false}
      initialUiState={{
        wp_searchable_posts: {
          query: getUrlParam(),
        },
      }}>
      <Configure analytics={false} hitsPerPage={16} />

      <div className="bg-blue-pale py-2xl">
        <div className="wrapper wrapper--sm">
          <SearchBox
            searchAsYouType={true}
            placeholder="Enter keyword..."
            classNames={{
              form: 'relative h-[64px]',
              input:
                'bg-white w-full h-full px-sm focus:outline-none focus:border focus:border-gold',
              resetIcon:
                'absolute right-[24px] top-1/2 transform -translate-y-[50%] w-[12px] h-[12px]',
            }}
          />
        </div>
      </div>

      <div className="wrapper wrapper--sm">
        <SearchResultsStats />

        <NoResultsBoundary fallback={<AlgoliaNoResults />}>
          <Hits hitComponent={SearchResultsHit} />
        </NoResultsBoundary>

        <footer className="mt-3xl">
          <AlgoliaIndexPagination />
        </footer>
      </div>
    </InstantSearch>
  )
}
